import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FaRobot, FaBrain, FaEye, FaCloud, FaLock, FaChartBar, FaCogs, FaCode, FaDatabase, FaNetworkWired } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SEO from '../../components/SEO';
import Spinner from '../../components/Spinner/Spinner';
import './Solutions.css';

// Icon mapping for dynamic icons
const iconMap = {
  FaBrain: <FaBrain />,
  FaEye: <FaEye />,
  FaNetworkWired: <FaNetworkWired />,
  FaCloud: <FaCloud />,
  FaLock: <FaLock />,
  FaChartBar: <FaChartBar />,
  FaDatabase: <FaDatabase />,
  FaCogs: <FaCogs />,
  FaRobot: <FaRobot />,
  FaCode: <FaCode />
};

// Kept between visits: coming back to the page shows the data at once, refreshed in the background
let cache = null;

const Solutions = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('rpa');
  const [rpaTools, setRpaTools] = useState(cache?.rpaTools || []);
  const [technologies, setTechnologies] = useState(cache?.technologies || []);
  const [industries, setIndustries] = useState(cache?.industries || []);
  const [loading, setLoading] = useState(!cache);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const currentLang = i18n.language || 'en';

  // Default data as fallback
  const defaultRpaTools = [
    {
      id: 'uipath',
      name_en: 'UiPath',
      name_fr: 'UiPath',
      description_en: 'Leading enterprise RPA platform',
      description_fr: 'Plateforme RPA d\'entreprise de premier plan',
      features_en: ['Studio Development', 'Orchestrator', 'AI Computer Vision', 'Document Understanding'],
      features_fr: ['Développement Studio', 'Orchestrator', 'Vision par ordinateur IA', 'Compréhension de documents'],
      logo: '/assets/images/logos/uipath-logo.png',
      color: '#FA4616',
      image: '/assets/images/digital-globe.jpg'
    },
    {
      id: 'blueprism',
      name_en: 'Blue Prism',
      name_fr: 'Blue Prism',
      description_en: 'Enterprise-grade intelligent automation',
      description_fr: 'Automatisation intelligente de niveau entreprise',
      features_en: ['Digital Workers', 'Control Room', 'Process Discovery', 'Cloud Deployment'],
      features_fr: ['Travailleurs numériques', 'Salle de contrôle', 'Découverte de processus', 'Déploiement cloud'],
      logo: null,
      color: '#1E3A8A',
      image: '/assets/images/business-clients.jpg'
    },
    {
      id: 'automation-anywhere',
      name_en: 'Automation Anywhere',
      name_fr: 'Automation Anywhere',
      description_en: 'Cloud-native intelligent automation platform',
      description_fr: 'Plateforme d\'automatisation intelligente cloud-native',
      features_en: ['Bot Insight', 'IQ Bot', 'AARI', 'Discovery Bot'],
      features_fr: ['Bot Insight', 'IQ Bot', 'AARI', 'Discovery Bot'],
      logo: null,
      color: '#FF6B00',
      image: '/assets/images/ai-technology.jpg'
    },
    {
      id: 'power-platform',
      name_en: 'Microsoft Power Platform',
      name_fr: 'Microsoft Power Platform',
      description_en: 'Low-code/no-code suite for automation and analytics',
      description_fr: 'Suite low-code/no-code pour l\'automatisation et l\'analyse',
      features_en: ['Power Automate', 'Power Apps', 'Power BI', 'Power Virtual Agents'],
      features_fr: ['Power Automate', 'Power Apps', 'Power BI', 'Power Virtual Agents'],
      logo: '/assets/images/logos/power-platform-logo.webp',
      color: '#0078D4',
      image: '/assets/images/data-analytics.jpg'
    }
  ];

  const defaultTechnologies = [
    {
      id: 'ai',
      name_en: 'Artificial Intelligence',
      name_fr: 'Intelligence Artificielle',
      description_en: 'AI and Machine Learning for intelligent automation',
      description_fr: 'IA et Machine Learning pour l\'automatisation intelligente',
      icon: 'FaBrain',
      technologies: ['TensorFlow', 'Azure Cognitive Services', 'AWS AI/ML', 'Google Cloud AI']
    },
    {
      id: 'ocr',
      name_en: 'Document Processing (OCR)',
      name_fr: 'Traitement de Documents (OCR)',
      description_en: 'Intelligent document extraction and processing',
      description_fr: 'Extraction et traitement intelligent de documents',
      icon: 'FaEye',
      technologies: ['ABBYY FlexiCapture', 'Tesseract', 'AWS Textract', 'Azure Form Recognizer']
    },
    {
      id: 'api',
      name_en: 'API Integration',
      name_fr: 'Intégration API',
      description_en: 'Seamless system connectivity and data exchange',
      description_fr: 'Connectivité système et échange de données transparents',
      icon: 'FaNetworkWired',
      technologies: ['REST APIs', 'GraphQL', 'Webhooks', 'Enterprise Service Bus']
    },
    {
      id: 'cloud',
      name_en: 'Cloud Solutions',
      name_fr: 'Solutions Cloud',
      description_en: 'Cloud infrastructure for your automations',
      description_fr: 'Infrastructure cloud pour vos automatisations',
      icon: 'FaCloud',
      technologies: ['Microsoft Azure', 'Amazon AWS', 'Google Cloud', 'Hybrid Cloud']
    },
    {
      id: 'security',
      name_en: 'Security & Compliance',
      name_fr: 'Sécurité & Conformité',
      description_en: 'Protection and regulatory compliance',
      description_fr: 'Protection et conformité réglementaire',
      icon: 'FaLock',
      technologies: ['Active Directory', 'OAuth 2.0', 'GDPR Compliance', 'SOC 2']
    },
    {
      id: 'analytics',
      name_en: 'Analytics & BI',
      name_fr: 'Analytics & BI',
      description_en: 'Performance analysis and reporting',
      description_fr: 'Analyse et reporting des performances',
      icon: 'FaChartBar',
      technologies: ['Power BI', 'Tableau', 'Qlik', 'Custom Dashboards']
    }
  ];

  const defaultIndustries = [
    {
      industry_en: 'Finance & Banking',
      industry_fr: 'Finance & Banque',
      icon: 'FaDatabase',
      useCases_en: ['Loan and credit processing', 'Risk management and compliance', 'Account reconciliation', 'Client onboarding'],
      useCases_fr: ['Traitement des prêts et crédits', 'Gestion des risques et conformité', 'Réconciliation comptable', 'Onboarding clients'],
      image: '/assets/images/ecommerce-shopping.jpg'
    },
    {
      industry_en: 'Insurance',
      industry_fr: 'Assurance',
      icon: 'FaLock',
      useCases_en: ['Claims management', 'Automated underwriting', 'Fraud detection', 'Policy renewals'],
      useCases_fr: ['Gestion des sinistres', 'Souscription automatisée', 'Détection de fraude', 'Renouvellement de polices'],
      image: '/assets/images/finance-reports.jpg'
    },
    {
      industry_en: 'Healthcare',
      industry_fr: 'Santé',
      icon: 'FaCogs',
      useCases_en: ['Appointment management', 'Invoice processing', 'Pharmaceutical inventory', 'Regulatory reporting'],
      useCases_fr: ['Gestion des rendez-vous', 'Traitement des factures', 'Gestion des stocks pharmaceutiques', 'Reporting réglementaire'],
      image: '/assets/images/ai-technology.jpg'
    },
    {
      industry_en: 'Manufacturing',
      industry_fr: 'Manufacturing',
      icon: 'FaRobot',
      useCases_en: ['Order management', 'Production planning', 'Quality control', 'Predictive maintenance'],
      useCases_fr: ['Gestion des commandes', 'Planification de production', 'Contrôle qualité', 'Maintenance prédictive'],
      image: '/assets/images/manufacturing.jpg'
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // RPA tools, technologies and industries, fetched in parallel
        const load = async (name) => {
          const snapshot = await getDocs(query(collection(db, name), orderBy('order', 'asc')));
          return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        };
        const [rpaData, techData, indData] = await Promise.all([
          load('solutions_rpa_tools'),
          load('solutions_technologies'),
          load('solutions_industries')
        ]);
        cache = {
          rpaTools: rpaData.length > 0 ? rpaData : defaultRpaTools,
          technologies: techData.length > 0 ? techData : defaultTechnologies,
          industries: indData.length > 0 ? indData : defaultIndustries
        };
        setRpaTools(cache.rpaTools);
        setTechnologies(cache.technologies);
        setIndustries(cache.industries);
      } catch (error) {
        console.error('Error fetching solutions data:', error);
        // Use defaults on error, unless a previous visit already loaded the real data
        if (!cache) {
          setRpaTools(defaultRpaTools);
          setTechnologies(defaultTechnologies);
          setIndustries(defaultIndustries);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to get localized content
  const getLocalizedContent = (item, field) => {
    const langField = `${field}_${currentLang}`;
    return item[langField] || item[`${field}_en`] || item[field] || '';
  };

  const getLocalizedArray = (item, field) => {
    const langField = `${field}_${currentLang}`;
    return item[langField] || item[`${field}_en`] || item[field] || [];
  };

  const isFr = currentLang === 'fr';

  // Rendered in the loading state too, so the prerendered page always has its meta tags
  const seo = (
    <SEO
      title={isFr ? 'Solutions - RPA, IA & Low-Code' : 'Solutions - RPA, AI & Low-Code'}
      description={isFr
        ? 'Découvrez nos solutions d\'automatisation : UiPath, Blue Prism, Automation Anywhere, Intelligence Artificielle et développement Low-Code.'
        : 'Discover our automation solutions: UiPath, Blue Prism, Automation Anywhere, Artificial Intelligence and Low-Code development.'
      }
      canonical="/solutions"
    />
  );

  if (loading) {
    return (
      <div className="solutions">
        {seo}
        <section className="solutions-hero">
          <div className="hero-background">
            <img
              src="/assets/images/ai-technology.jpg"
              alt="Solutions"
            />
            <div className="overlay"></div>
          </div>
          <div className="container">
            <motion.div
              className="solutions-hero-content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>{t('solutions.title')}</h1>
              <p className="hero-subtitle">{t('solutions.subtitle')}</p>
              <p className="hero-description">{t('solutions.description')}</p>
            </motion.div>
          </div>
        </section>
        <section className="solutions-content">
          <div className="container">
            <Spinner size="medium" text={currentLang === 'fr' ? 'Chargement des solutions...' : 'Loading solutions...'} />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="solutions">
      {seo}
      {/* Hero Section */}
      <section className="solutions-hero">
        <div className="hero-background">
          <img
            src="/assets/images/ai-technology.jpg"
            alt="Solutions"
          />
          <div className="overlay"></div>
        </div>
        <div className="container">
          <motion.div
            className="solutions-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>{t('solutions.title')}</h1>
            <p className="hero-subtitle">{t('solutions.subtitle')}</p>
            <p className="hero-description">{t('solutions.description')}</p>
          </motion.div>
        </div>
      </section>

      {/* Solutions Tabs */}
      <section className="solutions-content" ref={ref}>
        <div className="container">
          <div className="solutions-tabs">
            <button
              className={`tab-btn ${activeTab === 'rpa' ? 'active' : ''}`}
              onClick={() => setActiveTab('rpa')}
            >
              <FaRobot />
              <span>{currentLang === 'fr' ? 'Outils RPA' : 'RPA Tools'}</span>
            </button>
            <button
              className={`tab-btn ${activeTab === 'tech' ? 'active' : ''}`}
              onClick={() => setActiveTab('tech')}
            >
              <FaBrain />
              <span>{currentLang === 'fr' ? 'Technologies' : 'Technologies'}</span>
            </button>
            <button
              className={`tab-btn ${activeTab === 'industries' ? 'active' : ''}`}
              onClick={() => setActiveTab('industries')}
            >
              <FaCode />
              <span>{currentLang === 'fr' ? "Cas d'Usage" : 'Use Cases'}</span>
            </button>
          </div>

          {/* RPA Tools Tab */}
          {activeTab === 'rpa' && (
            <motion.div
              className="rpa-tools-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2>{t('solutions.rpaTools.title')}</h2>
              <div className="tools-grid">
                {rpaTools.map((tool, index) => (
                  <motion.div
                    key={tool.id}
                    className="tool-card"
                    initial={{ opacity: 0, y: 30 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    {(tool.logoUrl || tool.logo) && (
                      <div className="tool-logo">
                        <img src={tool.logoUrl || tool.logo} alt={getLocalizedContent(tool, 'name')} />
                      </div>
                    )}
                    <div className="tool-info">
                      <h3>{getLocalizedContent(tool, 'name')}</h3>
                      <p>{getLocalizedContent(tool, 'description')}</p>
                    </div>

                    <div className="tool-features">
                      <h4>{currentLang === 'fr' ? 'Fonctionnalités clés' : 'Key Features'}</h4>
                      <ul>
                        {getLocalizedArray(tool, 'features').map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Technologies Tab */}
          {activeTab === 'tech' && (
            <motion.div
              className="technologies-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2>{t('solutions.technologies.title')}</h2>
              <div className="tech-grid">
                {technologies.map((tech, index) => (
                  <motion.div
                    key={tech.id}
                    className="tech-card"
                    initial={{ opacity: 0, y: 30 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <div className="tech-icon">
                      {tech.iconUrl ? (
                        <img src={tech.iconUrl} alt="" className="tech-icon-img" />
                      ) : (
                        iconMap[tech.icon] || <FaCogs />
                      )}
                    </div>
                    <h3>{getLocalizedContent(tech, 'name')}</h3>
                    <p>{getLocalizedContent(tech, 'description')}</p>
                    <div className="tech-list">
                      {(tech.technologies || []).map((technology, idx) => (
                        <span key={idx} className="tech-tag">{technology}</span>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Industries Tab */}
          {activeTab === 'industries' && (
            <motion.div
              className="industries-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2>{currentLang === 'fr' ? "Cas d'Usage par Industrie" : 'Use Cases by Industry'}</h2>
              <div className="industries-grid">
                {industries.map((industry, index) => (
                  <motion.div
                    key={index}
                    className="industry-card"
                    initial={{ opacity: 0, y: 30 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <div className="industry-icon">
                      {industry.iconUrl ? (
                        <img src={industry.iconUrl} alt="" className="industry-icon-img" />
                      ) : (
                        iconMap[industry.icon] || <FaDatabase />
                      )}
                    </div>
                    <h3 className="industry-name">{getLocalizedContent(industry, 'industry')}</h3>
                    <ul className="use-cases">
                      {getLocalizedArray(industry, 'useCases').map((useCase, idx) => (
                        <li key={idx}>{useCase}</li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Process Section */}
      <section className="implementation-process">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2>{currentLang === 'fr' ? "Notre Processus d'Implémentation" : 'Our Implementation Process'}</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>{currentLang === 'fr' ? 'Analyse & Audit' : 'Analysis & Audit'}</h3>
                  <p className="step-description">{currentLang === 'fr' ? "Nous identifions les processus éligibles à l'automatisation, évaluons les gains potentiels et définissons les priorités selon le ROI attendu." : 'We identify processes eligible for automation, evaluate potential gains, and define priorities based on the expected ROI.'}</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>{currentLang === 'fr' ? 'Conception & Prototypage' : 'Design & Prototyping'}</h3>
                  <p className="step-description">{currentLang === 'fr' ? "Nous concevons les workflows, développons un premier prototype et validons rapidement la valeur ajoutée avec vos équipes." : 'We design the workflows, develop an initial prototype, and quickly validate the added value with your teams.'}</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>{currentLang === 'fr' ? 'Déploiement & Formation' : 'Deployment & Training'}</h3>
                  <p className="step-description">{currentLang === 'fr' ? "Nous mettons les robots en production, assurons leur intégration dans votre SI et formons vos équipes à leur utilisation." : 'We put the robots into production, ensure their integration into your IT environment, and train your teams to use them.'}</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>{currentLang === 'fr' ? 'Monitoring & Optimisation' : 'Monitoring & Optimization'}</h3>
                  <p className="step-description">{currentLang === 'fr' ? "Nous surveillons vos automatisations, détectons les axes d'amélioration et optimisons leur performance dans la durée." : 'We monitor your automations, identify improvement opportunities, and optimize their performance over time.'}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
